<template>
  <div class="rental-plan-step">
    <div class="content-box">
      <div class="content-box__title">
        <div class="icon"></div>
        <span>基础信息</span>
      </div>
      <div class="content-box__content">
        <div class="content-item basic-info">
          <p class="title">方案信息</p>
          <el-form
            ref="leaseBaseFormRef"
            :rules="leaseBaseRules"
            :model="leasePlanBase"
          >
            <column-box :column="2">
              <el-form-item label="名称" prop="name">
                <el-input
                  v-model="leasePlanBase.name"
                  clearable
                  placeholder="不对外展示，最多50字"
                  maxlength="50"
                />
              </el-form-item>
              <CarBrandSeriesModel
                v-model="leasePlanBase.brandSeriesModel"
                :brandSeriesModelName="leasePlanBase.brandSeriesModelName"
                :showModel="true"
                :filterScene="CAR_MODEL_FILTER_SCENE.IN_STOCK"
              />
              <el-form-item label="城市" prop="cityCodes">
                <QS-select
                  v-model="leasePlanBase.cityCodes"
                  clearable
                  filterable
                  multiple
                  collapse-tags
                  placeholder="请选择城市"
                  :option-data="allCities"
                  :option-config="{
                    labelKey: 'city',
                    valueKey: 'adcode',
                  }"
                  popper-class="plan-city-popper"
                />
              </el-form-item>
              <el-form-item label="车龄" prop="carAge">
                <el-select
                  v-model="leasePlanBase.carAge"
                  clearable
                  placeholder="请选择车龄"
                >
                  <el-option
                    v-for="item in carAgeOptions"
                    :label="item.name"
                    :value="item.code"
                    :key="item.code"
                  />
                </el-select>
              </el-form-item>
              <el-form-item label="是否有车证" prop="hasDrivingLicense">
                <el-radio-group v-model="leasePlanBase.hasDrivingLicense">
                  <el-radio
                    v-for="item in DRIVING_LICENSE_OPTIONS"
                    :label="item.value"
                    :key="item.value"
                    >{{ item.label }}</el-radio
                  >
                </el-radio-group>
              </el-form-item>
            </column-box>
          </el-form>
        </div>
        <div class="content-item">
          <p class="title">租赁规则</p>
          <personSchemeRentInfo
            ref="schemeRentInfo"
            :defaultRentInfo="leaseInfoParams"
            :tableData="defaultTable"
            @updateInfo="updateRentDateInfo"
            @formChange="formChange"
          />
        </div>
      </div>
    </div>
    <div class="content-box">
      <div class="content-box__title">
        <div class="icon"></div>
        <span>履约条款</span>
      </div>
      <div class="content-box__content">
        <DriverCooperationRules
          v-if="
            rentDateInfo.rentCategory === CATEGORY_CODE_MAP.SI_JI_HE_ZUO_MO_SHI
          "
          ref="driverCooperRef"
          :rentDateInfo="rentDateInfo"
          :payPlanFee="leaseInfoParams.payPlanFee"
          :sceneType="'leasePlan'"
          @autoAnchor="autoAnchor"
        />

        <div v-else-if="showRentPlan" class="content-item">
          <p class="title">租赁账单</p>
          <RentPlanConfig
            ref="RentPlanConfig"
            :defaultTableData="feeTableData"
            :rentDateInfo="rentDateInfo"
            :specialType="1"
            :scene="SCENE_TYPE.LEASE_PLAN"
            :configDisabled="false"
            @autoAnchor="autoAnchor"
            @tableDataChange="tableDataChange"
          />
        </div>

        <el-form
          ref="storeFormRef"
          :model="leasePlanBase"
          label-position="top"
          class="form-box"
        >
          <el-row :gutter="24">
            <el-col :span="12">
              <el-form-item label="签约主体">
                <span>{{ signCompany || '--' }}</span>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item
                label="可发车门店"
                prop="storeIds"
                :rules="[{ required: true, message: '请选择可发车门店' }]"
              >
                <div class="store-select-wrap">
                  <el-select
                    v-model="leasePlanBase.storeIds"
                    multiple
                    disabled
                    collapse-tags
                    placeholder="请选择门店"
                    class="store-name-select"
                  >
                    <el-option
                      v-for="item in storeTransferList"
                      :key="item.key"
                      :label="item.label"
                      :value="item.key"
                    />
                  </el-select>
                  <el-button
                    type="primary"
                    size="small"
                    :disabled="!leasePlanBase.cityCodes.length"
                    @click="storeDialogVisible = true"
                    >选择</el-button
                  >
                </div>
              </el-form-item>
            </el-col>
          </el-row>
        </el-form>

        <el-dialog
          :visible.sync="storeDialogVisible"
          title="选择可发车门店"
          :close-on-click-modal="false"
          class="store-transfer-dialog"
        >
          <el-transfer
            v-model="storeDialogSelectedIds"
            :data="storeTransferList"
            :titles="['未选择门店', '已选择门店']"
            filterable
          />
          <div slot="footer" class="dialog-footer">
            <el-button @click="storeDialogVisible = false">取消</el-button>
            <el-button type="primary" @click="confirmStoreSelection"
              >确定</el-button
            >
          </div>
        </el-dialog>

        <LeaseRules
          v-if="leasePlanBase.templateId"
          ref="leaseRulesRef"
          :rentDataInfo="rentDateInfo"
          :loading="leaseRulesLoading"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { getSimpleMerchantList } from '@/api/domain/organize.js'
import { checkBeforeSave, saveLeaseSchemeSignCheck } from '@/api/domain/leaseScheme'
import { depositAmountCheck } from '@/api/domain/purchase.js'
import { storeListAll } from '@/api/domain/shop'
import { getEnum } from '@/api/global'
import personSchemeRentInfo from '@/components/rentCalculateConfig/personSchemeRentInfo.vue'
import RentPlanConfig from '@/components/RentPlanConfig'
import DriverCooperationRules from '@/components/DriverCooperationRules'
import LeaseRules from '@/pages/orders/leaseScheme/personal/components/LeaseRules'
import {
  ADVANCE_PAYMENT_CODE,
  DEPOSIT_COLLECT_RULE,
  DEPOSIT_COLLECT_RULE_TEXT,
  RENT_TYPE,
  SCENE_TYPE,
} from '@/components/RentPlanConfig/config'
import {
  AMOUNTTYPE_CODE_MAP,
  CATEGORY_CODE_MAP,
  FEE_CODE_MAP,
  PAYMODE_CODE_MAP,
} from '@/store/dict/rentPlanDict'
import { PAY_MODE } from '@/config/rentConfig'
import { CAR_MODEL_FILTER_SCENE } from '@/config/carManage'
import { DRIVING_LICENSE_OPTIONS } from '@/pages/orders/leaseScheme/personal/const.js'
import { addition, formatNumberWithCommas } from '@/utils'
import moment from 'moment'
import { mapGetters } from 'vuex'

const createDefaultLeaseInfoParams = () => ({
  rentCategory: CATEGORY_CODE_MAP.ZU_CHE,
  rentPeriod: '',
  rentPeriodType: 3,
  payMode: '',
  signDate: '',
  startStopDate: [],
  isTranDeposit: '',
  contractDateText: '',
  rentPeriodText: '',
  allTotalAmount: '',
  deposit: '',
  rentAmount: '',
  firstPayment: '',
  fullPayment: '',
  otherAmount: '',
  payPlanFee: null,
})

const createDefaultRentDateInfo = () => ({
  rentCategory: CATEGORY_CODE_MAP.ZU_CHE,
  rentPeriod: '',
  rentPeriodType: 3,
  payMode: '',
  signDate: '',
  startStopDate: [],
  isTranDeposit: '',
  contractDateText: '',
  rentPeriodText: '',
  allTotalAmount: '',
  deposit: '',
  rentAmount: '',
  firstPayment: '',
  fullPayment: '',
  otherAmount: '',
})

const createDefaultLeasePlanBase = () => ({
  id: '',
  templateId: '',
  cityCodes: [],
  brandId: '',
  seriesId: '',
  modelId: '',
  cityNames: [],
  brandSeriesModel: {},
  brandSeriesModelName: {},
  name: '',
  carAge: '',
  hasDrivingLicense: null,
  storeIds: [],
})

const AI_EMPTY_VALUE_RE = /^(null|undefined|未明确|无|暂无|不详|--|-|空)$/i

export default {
  name: 'ContractTemplateLeasePlanForm',
  components: {
    personSchemeRentInfo,
    RentPlanConfig,
    DriverCooperationRules,
    LeaseRules,
  },
  props: {
    contractName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      SCENE_TYPE,
      CATEGORY_CODE_MAP,
      CAR_MODEL_FILTER_SCENE,
      leaseRulesLoading: false,
      leaseBaseRules: {
        name: [{ required: true, message: '请输入', trigger: 'blur' }],
        brandId: [{ required: true, message: '请选择', trigger: 'change' }],
        modelId: [{ required: true, message: '请选择', trigger: 'change' }],
        cityCodes: [
          { required: true, message: '请选择城市', trigger: 'change' },
        ],
        carAge: [{ required: true, message: '请选择车龄', trigger: 'change' }],
        hasDrivingLicense: [
          { required: true, message: '请选择是否有车证', trigger: 'change' },
        ],
      },
      leasePlanBase: createDefaultLeasePlanBase(),
      leaseInfoParams: createDefaultLeaseInfoParams(),
      rentDateInfo: createDefaultRentDateInfo(),
      payPlanFee: {},
      defaultTable: [],
      feeTableData: [],
      leasePlanPostParams: {},
      aiLeaseRulesData: null,
      aiSummaryRuleTips: {},
      aiAppliedLeaseInfo: {},
      aiAppliedBaseInfo: {},
      aiAppliedFeeSignatures: {},
      aiPlanNameBase: '',
      isApplyingAiPrefill: false,
      isNameAutoGenerated: true,
      isSettingAutoName: false,
      carAgeOptions: [],
      signCompany: '',
      storeTransferList: [],
      storeDialogVisible: false,
      storeDialogSelectedIds: [],
      DRIVING_LICENSE_OPTIONS,
      storeFetchTimer: null,
    }
  },
  provide() {
    return {
      isAdd: true,
      deposit: {
        flag: 0,
      },
      getPlanId: () => null,
      getOrderConfigInfo: () => ({}),
    }
  },
  computed: {
    ...mapGetters(['allCities']),
    suggestedPlanName() {
      return this.buildLivePlanNameBase()
    },
    showRentPlan() {
      return this.rentDateInfo.rentCategory && this.leasePlanBase.templateId
    },
  },
  watch: {
    contractName(val) {
      if (val && !this.leasePlanBase.name) {
        this.isSettingAutoName = true
        this.leasePlanBase.name = this.suggestedPlanName || val
        this.isSettingAutoName = false
      }
    },
    suggestedPlanName(val) {
      if (val && this.isNameAutoGenerated && val !== this.leasePlanBase.name) {
        this.isSettingAutoName = true
        this.leasePlanBase.name = val
        this.isSettingAutoName = false
      }
    },
    'leasePlanBase.name': function (val) {
      if (this.isSettingAutoName || this.isApplyingAiPrefill) return
      if (val && val !== this.suggestedPlanName) {
        this.isNameAutoGenerated = false
      }
    },
    'leasePlanBase.brandSeriesModel': {
      handler(val = {}) {
        const { brandId, seriesId, modelId } = val
        this.leasePlanBase.brandId = brandId
        this.leasePlanBase.seriesId = seriesId
        this.leasePlanBase.modelId = modelId
        this.$nextTick(() => {
          this.$refs.leaseBaseFormRef?.clearValidate?.('brandId')
          this.$refs.leaseBaseFormRef?.clearValidate?.('modelId')
        })
      },
      deep: true,
    },
    'leasePlanBase.templateId': {
      handler(val) {
        if (val && this.aiLeaseRulesData) {
          this.applyLeaseRuleData(this.aiLeaseRulesData)
        }
      },
    },
    storeDialogVisible(val) {
      if (val) {
        this.storeDialogSelectedIds = [...this.leasePlanBase.storeIds]
      }
    },
    'leasePlanBase.cityCodes': {
      handler(val) {
        clearTimeout(this.storeFetchTimer)
        if (val && val.length > 0) {
          this.storeFetchTimer = setTimeout(async () => {
            await this.fetchStoreByCity(val)
            this.leasePlanBase.storeIds = this.storeTransferList.map(
              (item) => item.key,
            )
          }, 300)
        } else {
          this.storeTransferList = []
          this.leasePlanBase.storeIds = []
        }
      },
    },
  },
  created() {
    if (this.allCities.length === 0) {
      this.$store.dispatch('getCityListFn')
    }
    this.fetchCarAgeOptions()
    this.fetchSignCompany()
  },
  beforeDestroy() {
    clearTimeout(this.storeFetchTimer)
  },
  methods: {
    // Public ref API used by the contract template editor.
    // eslint-disable-next-line vue/no-unused-properties
    initDefaults() {
      if (!this.leaseInfoParams.rentCategory) {
        this.leaseInfoParams.rentCategory = CATEGORY_CODE_MAP.ZU_CHE
      }
      if (!this.rentDateInfo.rentCategory) {
        this.rentDateInfo.rentCategory = CATEGORY_CODE_MAP.ZU_CHE
      }
      if (!this.leaseInfoParams.rentPeriodType) {
        this.leaseInfoParams.rentPeriodType = 3
      }
      if (!this.rentDateInfo.rentPeriodType) {
        this.rentDateInfo.rentPeriodType = 3
      }
      if (this.contractName && !this.leasePlanBase.name) {
        this.isNameAutoGenerated = true
        this.leasePlanBase.name = this.suggestedPlanName || this.contractName
      }
    },
    // Public ref API used by the contract template editor.
    // eslint-disable-next-line vue/no-unused-properties
    prepareAfterTemplateSaved(templateId) {
      this.leasePlanBase.templateId = templateId
      if (!this.leasePlanBase.name) {
        this.isNameAutoGenerated = true
        this.leasePlanBase.name = this.suggestedPlanName || this.contractName
      }
      if (this.aiLeaseRulesData) {
        this.applyLeaseRuleData(this.aiLeaseRulesData)
      }
    },
    // Public ref API used by the contract template editor.
    // eslint-disable-next-line vue/no-unused-properties
    resetForm() {
      this.$refs.leaseRulesRef?.setFormData?.(null)
      this.$refs.driverCooperRef?.clearFormData?.()
      this.leasePlanBase = createDefaultLeasePlanBase()
      this.leaseInfoParams = createDefaultLeaseInfoParams()
      this.rentDateInfo = createDefaultRentDateInfo()
      this.payPlanFee = {}
      this.defaultTable = []
      this.feeTableData = []
      this.leasePlanPostParams = {}
      this.aiLeaseRulesData = null
      this.aiSummaryRuleTips = {}
      this.aiAppliedLeaseInfo = {}
      this.aiAppliedBaseInfo = {}
      this.aiAppliedFeeSignatures = {}
      this.aiPlanNameBase = ''
      this.isApplyingAiPrefill = false
      this.isNameAutoGenerated = true
      this.storeTransferList = []
      this.storeDialogVisible = false
      this.storeDialogSelectedIds = []
    },
    updateRentDateInfo(val, feeTableData) {
      this.leaseInfoParams = {
        ...this.leaseInfoParams,
        ...val,
      }
      this.rentDateInfo = {
        ...this.rentDateInfo,
        ...val,
      }
      this.feeTableData = feeTableData || []
      if (val?.rentCategory === CATEGORY_CODE_MAP.SI_JI_HE_ZUO_MO_SHI) {
        if (val.payPlanFee) {
          this.payPlanFee = val.payPlanFee
        }
      } else {
        this.payPlanFee = null
      }
    },
    autoAnchor(toastFlag) {
      this.$refs.schemeRentInfo?.autoAnchorPoint?.(toastFlag)
    },
    formChange() {
      if (
        this.rentDateInfo.rentCategory === CATEGORY_CODE_MAP.SI_JI_HE_ZUO_MO_SHI
      ) {
        this.$refs.driverCooperRef?.clearFormData?.()
      }
    },
    tableDataChange(value = []) {
      const tableData = value || []
      const depositFeeData = tableData.find((item) => {
        return Number(item.fundType) === FEE_CODE_MAP.YA_JIN
      })
      if (depositFeeData) {
        this.rentDateInfo.deposit = depositFeeData.isConfirm
          ? `${formatNumberWithCommas(depositFeeData?.totalAmount || 0)} 元`
          : '--'
      }
      const rentAmountFeeData = tableData.find((item) => {
        return Number(item.fundType) === FEE_CODE_MAP.ZU_JIN
      })
      if (rentAmountFeeData) {
        const rentAmount =
          rentAmountFeeData.payPlanType !== 2
            ? `${formatNumberWithCommas(
              rentAmountFeeData?.totalAmount || 0,
            )} 元`
            : `${rentAmountFeeData?.totalAmount || 0}元`
        this.rentDateInfo.rentAmount = rentAmountFeeData.isConfirm
          ? rentAmount
          : '--'
      }
      const firstPaymentFeeData = tableData.find((item) => {
        return Number(item.fundType) === FEE_CODE_MAP.SHOU_FU_KUAN
      })
      if (firstPaymentFeeData) {
        this.rentDateInfo.firstPayment = firstPaymentFeeData.isConfirm
          ? `${formatNumberWithCommas(
            firstPaymentFeeData?.totalAmount || 0,
          )} 元`
          : '--'
      }
      const fullPaymentFeeData = tableData.find((item) => {
        return Number(item.fundType) === FEE_CODE_MAP.QUAN_KUAN
      })
      if (fullPaymentFeeData) {
        this.rentDateInfo.fullPayment = fullPaymentFeeData.isConfirm
          ? `${formatNumberWithCommas(fullPaymentFeeData?.totalAmount || 0)} 元`
          : '--'
      }
      const otherTableFeeList = tableData.filter((item) => {
        return (
          Number(item.fundType) !== FEE_CODE_MAP.ZU_JIN &&
          Number(item.fundType) !== FEE_CODE_MAP.YA_JIN &&
          Number(item.fundType) !== FEE_CODE_MAP.QUAN_KUAN &&
          Number(item.fundType) !== FEE_CODE_MAP.SHOU_FU_KUAN
        )
      })
      const noConfirmedOtherFee = otherTableFeeList.every(
        (i) => i.isConfirm === false,
      )
      if (!otherTableFeeList.length || noConfirmedOtherFee) {
        this.rentDateInfo.otherAmount = '--'
      } else {
        const otherTableFeeTotalAmount = otherTableFeeList.reduce(
          (sum, item) => {
            return addition(sum, item?.totalAmount || 0)
          },
          0,
        )
        this.rentDateInfo.otherAmount = `${formatNumberWithCommas(
          otherTableFeeTotalAmount,
        )} 元`
      }
    },
    validateLeaseBaseForm() {
      return new Promise((resolve) => {
        if (!this.$refs.leaseBaseFormRef?.validate) {
          resolve(false)
          return
        }
        this.$refs.leaseBaseFormRef.validate((valid) => {
          if (!valid) {
            this.$message.error('请先将【方案基本信息】的必填字段填写完整')
            this.$refs.leaseBaseFormRef?.$el?.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'start',
            })
          }
          resolve(valid)
        })
      })
    },
    async validateLeasePlanForm() {
      await this.$nextTick()

      const baseValid = await this.validateLeaseBaseForm()
      if (!baseValid) return false

      const rentInfoData = this.$refs.schemeRentInfo?.getFormData?.()
      if (!rentInfoData) return false

      let payPlanFee = null
      let tableData = null

      if (
        this.rentDateInfo.rentCategory === CATEGORY_CODE_MAP.SI_JI_HE_ZUO_MO_SHI
      ) {
        payPlanFee = await this.$refs.driverCooperRef?.getFormData?.()
        if (!payPlanFee) {
          this.$message.error('请填写司机规则带有红色 * 的内容')
          return false
        }
      } else {
        if (!this.$refs.RentPlanConfig) {
          this.$message.error('请先配置租赁账单')
          return false
        }
        tableData = this.$refs.RentPlanConfig.getFormData()
        if (!tableData) return false
        tableData = tableData.map((item) => {
          if (Number(item.fundType) === FEE_CODE_MAP.YA_JIN) {
            return {
              ...item,
              totalAmount: item.amount,
            }
          }
          return item
        })
      }

      const leaseRuleData = await this.$refs.leaseRulesRef?.validateForm?.()
      if (!leaseRuleData) return false
      const aiRuleTips = this.getSubmitAiRuleTips(leaseRuleData)

      // 门店校验
      const storeFormValid = await new Promise((resolve) => {
        if (this.$refs.storeFormRef) {
          this.$refs.storeFormRef.validate((valid) => resolve(valid))
        } else {
          resolve(true)
        }
      })
      if (!storeFormValid) {
        this.$message.error('请选择可发车门店')
        return false
      }

      const depositCheckValid = await this.validateFollowRentDeposit(
        tableData || [],
      )
      if (!depositCheckValid) return false

      const baseInfoData = { ...this.leasePlanBase }
      this.leasePlanPostParams = {
        name: baseInfoData.name,
        modelId: baseInfoData.modelId,
        templateId: baseInfoData.templateId || undefined,
        cityCodes: baseInfoData.cityCodes,
        age: baseInfoData.carAge,
        carCert: baseInfoData.hasDrivingLicense,
        payMode: rentInfoData.payMode,
        storeIdList: baseInfoData.storeIds,
        rentCategory: rentInfoData.rentCategory,
        rentPeriod: rentInfoData.rentPeriod,
        rentPeriodType: rentInfoData.rentPeriodType,
        rentFeeList: tableData,
        payPlanFee,
        state: 10,
        rentCarRule: leaseRuleData?.rentCarRule,
        returnCarRule: leaseRuleData?.returnCarRule,
        rentCarRuleTip: aiRuleTips.rentCarRuleTip,
        returnCarRuleTip: aiRuleTips.returnCarRuleTip,
      }
      return true
    },
    buildFollowRentDepositPreviewParams(depositItem = {}) {
      const payPlanType = depositItem.payPlanType ?? RENT_TYPE.FIXEDRENT
      const payMode =
        depositItem.fundType === FEE_CODE_MAP.YA_JIN &&
        Number(depositItem.depositCollectRule) ===
          DEPOSIT_COLLECT_RULE.FOLLOW_RENT
          ? depositItem.rentPayMode ||
            depositItem.payMode ||
            this.rentDateInfo.payMode
          : depositItem.payMode || this.rentDateInfo.payMode
      const commonParams = {
        rentPlanFeeType: depositItem.fundType,
        bizType: 10,
        startTime:
          depositItem.startTime || this.rentDateInfo.startStopDate?.[0],
        endTime: depositItem.endTime || this.rentDateInfo.startStopDate?.[1],
        payMode,
        customerDueDate: depositItem.customerDueDate || '',
      }
      const payPlanParams =
        payPlanType === RENT_TYPE.DYNAMICRENT
          ? {
            payPlanType,
            spId: depositItem.spId ?? '',
            dynamicType: depositItem.dynamicType ?? '',
            dynamicList: depositItem.dynamicList ?? [],
          }
          : {
            amountType: Number(depositItem.amountType ?? 0),
            amount:
                depositItem.fundType === FEE_CODE_MAP.YA_JIN &&
                Number(depositItem.depositCollectRule) ===
                  DEPOSIT_COLLECT_RULE.FOLLOW_RENT
                  ? depositItem.depositPeriodAmount ?? ''
                  : depositItem.amount ?? '',
          }
      const params = {
        ...commonParams,
        ...payPlanParams,
        rentCategory:
          depositItem.rentCategory || this.rentDateInfo.rentCategory,
        rentPeriod: depositItem.rentPeriod ?? this.rentDateInfo.rentPeriod,
        rentPeriodType:
          depositItem.rentPeriodType ?? this.rentDateInfo.rentPeriodType,
        renewal: 0,
      }

      if (
        depositItem.fundType === FEE_CODE_MAP.YA_JIN &&
        (Number(depositItem.depositCollectRule) ===
          DEPOSIT_COLLECT_RULE.CUSTOM_STAGES ||
          Number(depositItem.depositCollectRule) ===
            DEPOSIT_COLLECT_RULE.FOLLOW_RENT)
      ) {
        params.totalAmount = depositItem.amount ?? depositItem.totalAmount ?? ''
      }

      if (
        depositItem.fundType === FEE_CODE_MAP.YA_JIN &&
        Number(depositItem.depositCollectRule) ===
          DEPOSIT_COLLECT_RULE.FOLLOW_RENT
      ) {
        params.depositFirstPeriodAmount =
          depositItem.depositFirstPeriodAmount ?? ''
      }

      if (
        depositItem.fundType === FEE_CODE_MAP.YA_JIN &&
        Number(depositItem.depositCollectRule) ===
          DEPOSIT_COLLECT_RULE.CUSTOM_STAGES
      ) {
        params.depositInstallmentList = depositItem.depositInstallmentList ?? []
      }

      if (
        (depositItem.fundType === FEE_CODE_MAP.ZU_JIN ||
          depositItem.fundType === FEE_CODE_MAP.OTHERS) &&
        [
          PAY_MODE.WEEK,
          PAY_MODE.NATURAL_WEEK,
          PAY_MODE.FULL_MOON,
          PAY_MODE.NATURAL_MONTH,
        ].includes(Number(payMode)) &&
        depositItem.amountAccuracy !== undefined &&
        depositItem.amountAccuracy !== null
      ) {
        params.amountAccuracy = depositItem.amountAccuracy
      }

      if (
        this.rentDateInfo.preFreeRentDays !== undefined &&
        this.rentDateInfo.preFreeRentDays !== null
      ) {
        params.preFreeRentDays = this.rentDateInfo.preFreeRentDays
      }
      if (depositItem.scene !== undefined && depositItem.scene !== null) {
        params.scene = depositItem.scene
      }
      return params
    },
    async validateFollowRentDeposit(tableData = []) {
      const depositItem = tableData.find((item) => {
        return (
          Number(item.fundType) === FEE_CODE_MAP.YA_JIN &&
          Number(item.depositCollectRule) === DEPOSIT_COLLECT_RULE.FOLLOW_RENT
        )
      })
      if (!depositItem) return true
      try {
        const params = this.buildFollowRentDepositPreviewParams(depositItem)
        const res = await depositAmountCheck(params)
        if (res.code === 1 && res.data === false) {
          this.$message.error(
            res.msg || '押金校验不通过，请检查随租金周期收取配置',
          )
          return false
        }
        return true
      } catch (error) {
        return false
      }
    },
    async fetchCarAgeOptions() {
      try {
        const { code, data } = await getEnum({ type: 'carAgeEnum' })
        if (code === 1) {
          this.carAgeOptions = data || []
        }
      } catch {
        /* 静默失败 */
      }
    },
    async fetchStoreByCity(cityCodes) {
      try {
        const { code, data } = await storeListAll({ cityCodes })
        if (code === 1) {
          this.storeTransferList = (data || []).map((item) => ({
            key: item.id,
            label: item.name,
          }))
        }
      } catch {
        /* 静默失败 */
      }
    },
    async fetchSignCompany() {
      const { code, data } = await getSimpleMerchantList({ defaultType: 0 })
      if (code === 1 && data && data.length) {
        this.signCompany = data[0].corporationName || ''
      }
    },
    confirmStoreSelection() {
      this.leasePlanBase.storeIds = [...this.storeDialogSelectedIds]
      this.storeDialogVisible = false
    },
    // Public ref API used by the contract template editor.
    // eslint-disable-next-line vue/no-unused-properties
    async submit() {
      const valid = await this.validateLeasePlanForm()
      if (!valid) return false

      const postParams = { ...this.leasePlanPostParams }

      try {
        const checkRes = await checkBeforeSave(postParams)
        if (checkRes.code !== 1 || checkRes.data === false) {
          this.$message.error(checkRes.msg || '方案排重校验不通过')
          return false
        }
      } catch {
        return false
      }

      try {
        const res = await saveLeaseSchemeSignCheck(postParams)
        if (res.code === 1) {
          this.$message.success('添加成功')
          return true
        }
      } catch {
        return false
      }
      return false
    },
    // Public ref API used by the contract template editor.
    // eslint-disable-next-line vue/no-unused-properties
    applyAiResult(analysisData = {}, summaryData = null) {
      const isFirstPass = !summaryData
      if (isFirstPass) {
        this.resetForm()
      }
      this.cacheAiSummaryRuleTips(summaryData)
      this.applyAiExtractedLeaseInfo(analysisData, summaryData)
      this.applyAiLeaseRules(analysisData, summaryData)
      if (isFirstPass && !this.feeTableData.length) {
        this.$nextTick(() => {
          this.$refs.schemeRentInfo?.queryFeeList?.(
            (feeList) => this.$refs.schemeRentInfo?.addRequiredFee?.(feeList),
          )
        })
      }
    },
    cacheAiSummaryRuleTips(summaryData = null) {
      if (summaryData === undefined || summaryData === null) {
        this.aiSummaryRuleTips = {}
        return
      }
      const parsedSummary = this.parseMaybeJson(summaryData)
      if (!parsedSummary || typeof parsedSummary !== 'object') {
        this.aiSummaryRuleTips = {}
        return
      }
      this.aiSummaryRuleTips = {
        rentCarRuleTip: this.cloneAiValue(parsedSummary.rentCarRuleTip),
        returnCarRuleTip: this.cloneAiValue(parsedSummary.returnCarRuleTip),
      }
    },
    getSubmitAiRuleTips(leaseRuleData = {}) {
      const hasSummaryRentTip =
        this.aiSummaryRuleTips.rentCarRuleTip !== undefined
      const hasSummaryReturnTip =
        this.aiSummaryRuleTips.returnCarRuleTip !== undefined
      return {
        rentCarRuleTip: hasSummaryRentTip
          ? this.cloneAiValue(this.aiSummaryRuleTips.rentCarRuleTip)
          : leaseRuleData?.rentCarRuleTip,
        returnCarRuleTip: hasSummaryReturnTip
          ? this.cloneAiValue(this.aiSummaryRuleTips.returnCarRuleTip)
          : leaseRuleData?.returnCarRuleTip,
      }
    },
    applyAiExtractedLeaseInfo(analysisData = {}, summaryData = null) {
      const prefill = this.buildAiLeasePlanPrefill(analysisData, summaryData)
      this.applyAiLeasePlanPrefill(prefill)
    },
    buildAiLeasePlanPrefill(analysisData = {}, summaryData = null) {
      const fieldExtract = this.parseMaybeJson(analysisData.fieldExtract) || {}
      const fieldMappings = this.getAiFieldMappings(fieldExtract, analysisData)
      const keyInfo = this.getAiKeyInfo(analysisData, summaryData)
      const aiSource = {
        ...fieldExtract,
        fieldMappings,
        keyInfo,
        analysisData,
        summaryData,
      }
      const prefill = {
        baseInfo: {},
        leaseInfo: {},
        amounts: {},
      }

      const signDate = this.normalizeAiDateTime(
        this.getAiValue(aiSource, [
          'signDate',
          'contractSignDate',
          '合同签署日期',
        ]) ||
          this.getAiFieldMappingValue(
            { fieldMappings },
            '合同信息',
            '合同签署日期',
          ) ||
          this.pickAiKeyInfoValue(keyInfo.contract_info, [
            '合同签署日期',
            '签署日期',
          ]),
      )
      const startTime = this.normalizeAiDateTime(
        this.getAiValue(aiSource, [
          'startTime',
          'contractStartDate',
          '合同开始时间',
        ]) ||
          this.getAiFieldMappingValue(
            { fieldMappings },
            '合同信息',
            '合同开始时间',
          ) ||
          this.pickAiKeyInfoValue(keyInfo.contract_info, [
            '合同开始时间',
            '开始时间',
          ]),
      )
      const endTime = this.normalizeAiDateTime(
        this.getAiValue(aiSource, [
          'endTime',
          'contractEndDate',
          '合同结束时间',
        ]) ||
          this.getAiFieldMappingValue(
            { fieldMappings },
            '合同信息',
            '合同结束时间',
          ) ||
          this.pickAiKeyInfoValue(keyInfo.contract_info, [
            '合同结束时间',
            '结束时间',
          ]),
        true,
      )
      if (signDate) prefill.leaseInfo.signDate = signDate
      if (startTime && endTime) prefill.leaseInfo.startStopDate = [startTime, endTime]

      const vehicleModel = this.cleanAiText(
        this.getAiFieldMappingValue(
          { fieldMappings },
          '车辆信息',
          '品牌车型',
        ) ||
          this.getAiFieldMappingValue({ fieldMappings }, '车辆信息', '车型') ||
          this.getAiValue(aiSource, [
            'vehicleModel',
            'modelName',
            '品牌车型',
            '车型',
          ]),
      )
      if (vehicleModel) prefill.vehicleModel = vehicleModel

      const brandId = this.parseAiNumber(
        this.getAiValue(aiSource, ['brandId', '品牌ID']),
      )
      const seriesId = this.parseAiNumber(
        this.getAiValue(aiSource, ['seriesId', '车系ID']),
      )
      const modelId = this.parseAiNumber(
        this.getAiValue(aiSource, ['modelId', '车型ID']),
      )
      if (brandId) prefill.baseInfo.brandId = Number(brandId)
      if (seriesId) prefill.baseInfo.seriesId = Number(seriesId)
      if (modelId) prefill.baseInfo.modelId = Number(modelId)
      if (brandId || seriesId || modelId) {
        prefill.baseInfo.brandSeriesModel = {
          brandId: brandId ? Number(brandId) : '',
          seriesId: seriesId ? Number(seriesId) : '',
          modelId: modelId ? Number(modelId) : '',
        }
      }

      const brandName = this.cleanAiText(
        this.getAiValue(aiSource, ['brandName', '品牌']),
      )
      const seriesName = this.cleanAiText(
        this.getAiValue(aiSource, ['seriesName', '车系']),
      )
      if (brandName || seriesName || vehicleModel) {
        prefill.baseInfo.brandSeriesModelName = {
          brandName,
          seriesName,
          modelName: vehicleModel,
        }
      }

      const cityText = this.cleanAiText(
        this.getAiValue(aiSource, [
          'city',
          'cityName',
          '方案所属城市',
          '城市',
        ]) ||
          this.extractAiCityFromAddress(
            this.getAiFieldMappingValue(
              { fieldMappings },
              '出租方信息',
              '出租方地址',
            ) ||
              this.getAiFieldMappingValue(
                { fieldMappings },
                '客户信息',
                '客户联系地址',
              ) ||
              this.getAiFieldMappingValue(
                { fieldMappings },
                '客户信息',
                '客户地址',
              ),
          ),
      )
      const cityCodes = this.resolveAiCityCodes(cityText)
      if (cityCodes.length) {
        prefill.baseInfo.cityCodes = cityCodes
      }

      const paymentFields = this.getAiPaymentFields(fieldMappings)
      const rentCategory = this.inferAiRentCategory(aiSource, paymentFields)
      if (rentCategory) prefill.leaseInfo.rentCategory = rentCategory

      const rentPeriodInfo = this.parseAiRentPeriod(
        this.getAiValue(aiSource, [
          'rentPeriod',
          'leasePeriod',
          'period',
          '租期',
          '租赁周期',
          '预计租赁周期',
          '合同期限',
        ]) ||
          this.getAiFieldMappingValue(
            { fieldMappings },
            '合同信息',
            '租赁期数',
          ) ||
          this.getAiFieldMappingValue(
            { fieldMappings },
            '合同信息',
            '租金期数',
          ) ||
          this.pickAiKeyInfoValue(keyInfo.contract_info, [
            '租赁期数',
            '租金期数',
            '租期',
          ]),
        this.getAiValue(aiSource, [
          'rentPeriodType',
          'periodType',
          '租期单位',
          '租赁周期单位',
        ]),
      )
      if (rentPeriodInfo.rentPeriod) prefill.leaseInfo.rentPeriod = String(rentPeriodInfo.rentPeriod)
      if (rentPeriodInfo.rentPeriodType) prefill.leaseInfo.rentPeriodType = rentPeriodInfo.rentPeriodType

      const payMode = this.parseAiPayMode(
        this.getAiValue(aiSource, [
          'payMode',
          'payModeName',
          'paymentMode',
          'paymentCycle',
          '收款方式',
          '付款方式',
          '支付方式',
          '结算周期',
          '支付周期',
        ]) ||
          this.getAiFieldMappingValue(
            { fieldMappings },
            '合同信息',
            '租金支付频率',
          ) ||
          this.pickAiKeyInfoValue(keyInfo.contract_info, [
            '租金支付频率',
            '收款方式',
            '支付周期',
          ]),
      )
      if (payMode) prefill.leaseInfo.payMode = payMode

      const presentDays = this.parseAiNumber(
        this.getAiValue(aiSource, ['presentDays', 'freeDays', '赠送天数']) ||
          this.getAiFieldMappingValue({ fieldMappings }, '合同信息', '赠送天数'),
      )
      const preFreeRentDays = this.parseAiNumber(
        this.getAiValue(aiSource, [
          'preFreeRentDays',
          'frontFreeDays',
          '前置免租天数',
          '免租天数',
        ]),
      )
      if (presentDays !== '') prefill.leaseInfo.presentDays = String(presentDays)
      if (preFreeRentDays !== '') prefill.leaseInfo.preFreeRentDays = String(preFreeRentDays)

      prefill.amounts.deposit = this.parseAiAmountText(paymentFields.deposit)
      prefill.amounts.rentAmount = this.parseAiAmountText(
        paymentFields.rentAmount,
      )
      prefill.amounts.totalRent = this.parseAiAmountText(
        paymentFields.totalRent,
      )
      prefill.amounts.downPayment = this.parseAiAmountText(
        paymentFields.downPayment,
      )
      prefill.amounts.tailPayment = this.parseAiAmountText(
        paymentFields.tailPayment,
      )
      prefill.amounts.fullPayment = this.parseAiAmountText(
        paymentFields.fullPayment,
      )
      prefill.amounts.depositPaymentMethod = this.parseAiDepositPaymentMethod(
        paymentFields.depositPlan,
      )
      prefill.amounts.flowCommission = this.cleanAiText(
        paymentFields.flowCommission,
      )
      prefill.amounts.cooperationMode = this.cleanAiText(
        paymentFields.cooperationMode,
      )

      const directFeeList = this.getAiValue(aiSource, [
        'rentFeeList',
        'orderPlanFeeList',
        'feeTableData',
        'leaseFeeList',
        '收费项',
        '租赁账单',
      ])
      prefill.feeTableData = this.buildAiFeeTableData(prefill, directFeeList)
      prefill.planNameBase = this.buildAiPlanNameBase(prefill)
      return prefill
    },
    applyAiLeasePlanPrefill(prefill = {}) {
      if (!prefill || typeof prefill !== 'object') return
      this.isApplyingAiPrefill = true
      try {
        this.applyAiBaseInfoPrefill(prefill)
        this.applyAiLeaseInfoPrefill(prefill)
        this.applyAiFeeTablePrefill(prefill)
        this.applyAiAmountPreviewPrefill(prefill)
      } finally {
        this.$nextTick(() => {
          this.isApplyingAiPrefill = false
        })
      }
    },
    applyAiBaseInfoPrefill(prefill = {}) {
      const nextBase = {}
      const baseInfo = prefill.baseInfo || {}
      const baseKeys = [
        'brandId',
        'seriesId',
        'modelId',
        'cityCodes',
        'brandSeriesModel',
        'brandSeriesModelName',
      ]
      baseKeys.forEach((key) => {
        const value = baseInfo[key]
        if (
          this.shouldApplyAiValue(
            this.leasePlanBase[key],
            value,
            this.aiAppliedBaseInfo[key],
            createDefaultLeasePlanBase()[key],
          )
        ) {
          nextBase[key] = value
          this.aiAppliedBaseInfo[key] = this.cloneAiValue(value)
        }
      })

      const planName = this.makeUniqueAiPlanName(prefill.planNameBase)
      if (planName && this.shouldApplyAiPlanName()) {
        nextBase.name = planName
        this.aiAppliedBaseInfo.name = planName
        this.aiPlanNameBase = prefill.planNameBase || planName
      }

      if (Object.keys(nextBase).length) {
        this.leasePlanBase = {
          ...this.leasePlanBase,
          ...nextBase,
        }
      }
    },
    applyAiLeaseInfoPrefill(prefill = {}) {
      const nextLeaseInfo = {}
      const defaultLeaseInfo = createDefaultLeaseInfoParams()
      Object.entries(prefill.leaseInfo || {}).forEach(([key, value]) => {
        if (
          this.shouldApplyAiValue(
            this.leaseInfoParams[key],
            value,
            this.aiAppliedLeaseInfo[key],
            defaultLeaseInfo[key],
          )
        ) {
          nextLeaseInfo[key] = value
          this.aiAppliedLeaseInfo[key] = this.cloneAiValue(value)
        }
      })
      if (!Object.keys(nextLeaseInfo).length) return
      this.leaseInfoParams = {
        ...this.leaseInfoParams,
        ...nextLeaseInfo,
      }
      this.rentDateInfo = {
        ...this.rentDateInfo,
        ...nextLeaseInfo,
      }
    },
    applyAiFeeTablePrefill(prefill = {}) {
      const rows = prefill.feeTableData || []
      if (!rows.length) return
      const merged = this.mergeAiFeeRows(this.feeTableData, rows)
      this.defaultTable = merged
      this.feeTableData = merged
    },
    applyAiAmountPreviewPrefill(prefill = {}) {
      const amounts = prefill.amounts || {}
      this.rentDateInfo = {
        ...this.rentDateInfo,
        rentAmount: amounts.rentAmount
          ? `${formatNumberWithCommas(amounts.rentAmount)} 元/期`
          : this.rentDateInfo.rentAmount,
        allTotalAmount: amounts.totalRent
          ? `${formatNumberWithCommas(amounts.totalRent)} 元`
          : this.rentDateInfo.allTotalAmount,
        deposit: amounts.deposit
          ? `${formatNumberWithCommas(amounts.deposit)} 元`
          : this.rentDateInfo.deposit,
        firstPayment: amounts.downPayment
          ? `${formatNumberWithCommas(amounts.downPayment)} 元`
          : this.rentDateInfo.firstPayment,
        fullPayment: amounts.fullPayment
          ? `${formatNumberWithCommas(amounts.fullPayment)} 元`
          : this.rentDateInfo.fullPayment,
      }
    },
    getAiFieldMappings(fieldExtract = {}, analysisData = {}) {
      return (
        fieldExtract.fieldMappings ||
        fieldExtract.field_mappings ||
        analysisData.fieldMappings ||
        analysisData.field_mappings ||
        {}
      )
    },
    getAiKeyInfo(analysisData = {}, summaryData = null) {
      const parsedSummary = this.parseMaybeJson(summaryData) || {}
      const parsedAnalysisKeyInfo =
        this.parseMaybeJson(analysisData.keyInfo || analysisData.key_info) || {}
      const candidates = [
        parsedSummary.key_info,
        parsedSummary.keyInfo,
        parsedSummary,
        parsedAnalysisKeyInfo.key_info,
        parsedAnalysisKeyInfo.keyInfo,
        parsedAnalysisKeyInfo,
      ]
      return (
        candidates.find((item) => {
          return item && typeof item === 'object' && Object.keys(item).length
        }) || {}
      )
    },
    getAiFieldMappingEntry(fieldExtract, groupName, fieldName) {
      const fieldMappings = this.getAiFieldMappings(fieldExtract)
      const group = fieldMappings?.[groupName]
      if (!group || typeof group !== 'object') return null
      const normalizedFieldName = this.normalizeAiLookupKey(fieldName)
      for (const key in group) {
        const normalizedKey = this.normalizeAiLookupKey(
          String(key).replace(/[【】]/g, ''),
        )
        if (
          normalizedKey.includes(normalizedFieldName) ||
          normalizedFieldName.includes(normalizedKey)
        ) {
          return group[key]
        }
      }
      return null
    },
    extractAiEntryValue(entry) {
      if (entry === undefined || entry === null) return ''
      if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
        const candidates = [
          entry.value,
          entry.content,
          entry.text,
          entry.result,
          entry.amount,
          entry.date,
          entry.raw && entry.raw.value,
        ]
        for (const value of candidates) {
          const text = this.cleanAiText(value)
          if (text) return text
        }
        return ''
      }
      return this.cleanAiText(entry)
    },
    cleanAiText(value) {
      if (value === undefined || value === null) return ''
      if (Array.isArray(value)) {
        return value
          .map((item) => this.cleanAiText(item))
          .filter(Boolean)
          .join('、')
      }
      if (value && typeof value === 'object') return ''
      const text = String(value).trim()
      if (!text || AI_EMPTY_VALUE_RE.test(text)) return ''
      return text
    },
    pickAiKeyInfoValue(obj, keys = []) {
      if (!obj || typeof obj !== 'object') return ''
      for (const key of keys) {
        const value = this.extractAiEntryValue(obj[key])
        if (value) return value
      }
      const normalizedKeys = keys.map((key) => this.normalizeAiLookupKey(key))
      for (const [key, value] of Object.entries(obj)) {
        const normalized = this.normalizeAiLookupKey(key)
        if (
          normalizedKeys.some(
            (item) => normalized.includes(item) || item.includes(normalized),
          )
        ) {
          const text = this.extractAiEntryValue(value)
          if (text) return text
        }
      }
      return ''
    },
    getAiPaymentFields(fieldMappings = {}) {
      const source = { fieldMappings }
      const getPaymentField = (fieldName) =>
        this.getAiFieldMappingValue(source, '收款项', fieldName)
      return {
        deposit: getPaymentField('押金'),
        rentAmount: getPaymentField('每期租金') || getPaymentField('租金'),
        totalRent: getPaymentField('租金总额'),
        downPayment: getPaymentField('首付款'),
        tailPayment: getPaymentField('尾款'),
        fullPayment: getPaymentField('全款'),
        depositPlan: getPaymentField('押金支付计划'),
        flowCommission:
          getPaymentField('流水分佣') ||
          this.getAiFieldMappingValue(source, '合同信息', '流水分佣'),
        cooperationMode:
          getPaymentField('合作模式') ||
          this.getAiFieldMappingValue(source, '合同信息', '合作模式'),
        downPaymentEntry: this.getAiFieldMappingEntry(
          source,
          '收款项',
          '首付款',
        ),
        tailPaymentEntry: this.getAiFieldMappingEntry(source, '收款项', '尾款'),
        fullPaymentEntry: this.getAiFieldMappingEntry(source, '收款项', '全款'),
      }
    },
    inferAiRentCategory(aiSource = {}, paymentFields = {}) {
      const direct = this.parseAiRentCategory(
        this.getAiValue(aiSource, [
          'rentCategory',
          'rentCategoryName',
          '租赁类型',
          '租赁类别',
          '租赁模式',
          '类别',
        ]),
      )
      if (direct) return direct
      if (paymentFields.flowCommission || paymentFields.cooperationMode) {
        return CATEGORY_CODE_MAP.SI_JI_HE_ZUO_MO_SHI
      }
      const hasPurchasePayment =
        this.isPurchasePaymentEvidence(
          '首付款',
          paymentFields.downPayment,
          paymentFields.downPaymentEntry,
        ) ||
        this.isPurchasePaymentEvidence(
          '全款',
          paymentFields.fullPayment,
          paymentFields.fullPaymentEntry,
        ) ||
        this.isPurchasePaymentEvidence(
          '尾款',
          paymentFields.tailPayment,
          paymentFields.tailPaymentEntry,
        )
      if (hasPurchasePayment) return CATEGORY_CODE_MAP.YI_ZU_DAI_GOU
      if (paymentFields.deposit || paymentFields.rentAmount) return CATEGORY_CODE_MAP.ZU_CHE
      return ''
    },
    paymentSourceText(entry) {
      if (!entry || typeof entry !== 'object') return ''
      return String(
        entry.source_text ||
          entry.sourceText ||
          (entry.raw && (entry.raw.source_text || entry.raw.sourceText)) ||
          '',
      )
    },
    isPurchasePaymentEvidence(fieldName, value, entry) {
      if (!this.cleanAiText(value)) return false
      const source = this.paymentSourceText(entry).replace(/\s+/g, '')
      if (!source) return true
      if (/(押金|保证金|履约保证金|风险保证金|车辆保证金)/.test(source)) return false
      if (
        /(事故|维修|保险|理赔|赔付|损失|违约|账面净值)/.test(source) &&
        /买断/.test(source)
      ) return false
      if (fieldName === '首付款') return /(首付款|首付|购车定金|买断定金)/.test(source)
      if (fieldName === '尾款') return /(尾款|购车余款|买断余款|剩余购车款)/.test(source)
      if (fieldName === '全款') return /(全款|购车款|买断款|买断价|车辆转让款)/.test(source)
      return false
    },
    parseAiAmountText(value) {
      const text = this.cleanAiText(value).replace(/,/g, '')
      if (!text) return ''
      const match = text.match(/\d+(?:\.\d+)?/)
      if (!match) return ''
      const num = Number(match[0])
      if (!Number.isFinite(num) || num <= 0) return ''
      return match[0]
    },
    parseAiDepositPaymentMethod(value) {
      const text = this.cleanAiText(value)
      if (!text) return ''
      if (
        /(分期|后补|每月|每周|每期|按月|按周|分.*期|补.*元|后.*付)/.test(text)
      ) return 'stages'
      if (/(一次性|一次付清|全额一次|一次.*付清)/.test(text)) return 'once'
      return ''
    },
    extractAiCityFromAddress(address) {
      const text = this.cleanAiText(address)
      if (!text) return ''
      const match = text.match(/[^\s,，]+?(?:市|州)/)
      return match ? match[0] : ''
    },
    resolveAiCityCodes(cityText) {
      const target = this.cleanAiText(cityText)
      if (!target) return []
      const cities = []
      const walk = (list = []) => {
        list.forEach((item) => {
          if (!item || typeof item !== 'object') return
          cities.push(item)
          const children = item.children || item.childList || item.list || []
          if (Array.isArray(children) && children.length) walk(children)
        })
      }
      walk(this.allCities || [])
      const normalizedTarget = this.normalizeAiLookupKey(target)
      const matched = cities.find((item) => {
        const name = item.city || item.cityName || item.name || item.label || ''
        const normalizedName = this.normalizeAiLookupKey(name)
        return (
          normalizedName &&
          (normalizedName.includes(normalizedTarget) ||
            normalizedTarget.includes(normalizedName))
        )
      })
      const code =
        matched &&
        (matched.adcode || matched.cityCode || matched.code || matched.value)
      return code ? [code] : []
    },
    shouldApplyAiPlanName() {
      const name = this.cleanAiText(this.leasePlanBase.name)
      if (!name) return true
      if (this.aiAppliedBaseInfo.name && name === this.aiAppliedBaseInfo.name) return true
      if (this.contractName && name === this.contractName) return true
      return false
    },
    buildLivePlanNameBase() {
      const vehicleModel = this.cleanAiText(
        this.leasePlanBase.brandSeriesModelName?.modelName || '',
      )
      const leaseInfo = {
        rentCategory: this.leaseInfoParams.rentCategory || this.rentDateInfo.rentCategory,
        rentPeriod: this.leaseInfoParams.rentPeriod || this.rentDateInfo.rentPeriod,
        rentPeriodType: this.leaseInfoParams.rentPeriodType ?? this.rentDateInfo.rentPeriodType,
        payMode: this.leaseInfoParams.payMode || this.rentDateInfo.payMode,
        signDate: this.leaseInfoParams.signDate || this.rentDateInfo.signDate,
      }
      const amounts = {
        rentAmount: this.parseAiAmountText(
          this.leaseInfoParams.rentAmount || this.rentDateInfo.rentAmount,
        ),
        downPayment: this.parseAiAmountText(
          this.leaseInfoParams.firstPayment || this.rentDateInfo.firstPayment,
        ),
        fullPayment: this.parseAiAmountText(
          this.leaseInfoParams.fullPayment || this.rentDateInfo.fullPayment,
        ),
      }
      if (leaseInfo.rentCategory === CATEGORY_CODE_MAP.SI_JI_HE_ZUO_MO_SHI) {
        amounts.cooperationMode =
          this.cleanAiText(this.leaseInfoParams.payPlanFee?.cooperationMode) ||
          this.cleanAiText(this.leaseInfoParams.flowCommission)
      }
      const prefill = { vehicleModel, leaseInfo, amounts }
      return this.buildAiPlanNameBase(prefill)
    },
    buildAiPlanNameBase(prefill = {}) {
      const leaseInfo = prefill.leaseInfo || {}
      const amounts = prefill.amounts || {}
      const category =
        leaseInfo.rentCategory ||
        this.rentDateInfo.rentCategory ||
        CATEGORY_CODE_MAP.ZU_CHE
      const vehicleModel = this.cleanAiText(prefill.vehicleModel)
      const payModeName = this.formatAiPlanPayModeName(
        leaseInfo.payMode || this.rentDateInfo.payMode,
      )
      const rentPeriod = this.formatAiPlanRentPeriod(leaseInfo, payModeName)
      const { rentAmount } = amounts
      const signDate =
        this.extractValidDateString(leaseInfo.signDate) ||
        this.extractValidDateString(this.leaseInfoParams.signDate) ||
        this.extractValidDateString(this.rentDateInfo.signDate)
      const fallbackDate = signDate || this.getDefaultPlanNameTimestamp()
      if (category === CATEGORY_CODE_MAP.SI_JI_HE_ZUO_MO_SHI) {
        const cooperation =
          amounts.cooperationMode || amounts.flowCommission || fallbackDate
        return [vehicleModel, '司机合作', cooperation].filter(Boolean).join('-')
      }
      if (category === CATEGORY_CODE_MAP.YI_ZU_DAI_GOU) {
        const parts = [vehicleModel, '以租代购']
        if (!vehicleModel) parts.push(signDate)
        if (amounts.downPayment) parts.push(`${amounts.downPayment}首付`)
        if (amounts.fullPayment && !amounts.downPayment) parts.push(`${amounts.fullPayment}全款`)
        if (rentAmount && rentPeriod) parts.push(`${rentAmount}x${rentPeriod}`)
        if (!rentAmount && rentPeriod) parts.push(`${rentPeriod}${payModeName}`)
        if (amounts.tailPayment) parts.push(`${amounts.tailPayment}尾款`)
        return parts.filter(Boolean).join('-')
      }
      if (vehicleModel && rentPeriod && rentAmount) {
        return `${vehicleModel}-${rentPeriod}${payModeName}-${rentAmount}${this.getAiPlanRentUnit(
          payModeName,
          rentPeriod,
        )}`
      }
      if (vehicleModel && rentPeriod) return `${vehicleModel}-${rentPeriod}${payModeName}`
      if (vehicleModel) return `${vehicleModel}-纯租`
      return `未命名方案-${fallbackDate}`
    },
    getDefaultPlanNameTimestamp() {
      const now = new Date()
      const pad = (num) => String(num).padStart(2, '0')
      return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`
    },
    extractValidDateString(value) {
      if (!value) return ''
      if (value instanceof Date || (typeof value === 'object' && value !== null && !Array.isArray(value))) {
        try {
          const m = moment(value)
          if (m.isValid()) return m.format('YYYYMMDD')
        } catch (e) { /* ignore */ }
        return ''
      }
      const text = this.cleanAiText(value)
      if (!text || /^\[object Object\]/.test(text) || text.length < 4) return ''
      return text
    },
    formatAiPlanPayModeName(payMode) {
      const value = Number(payMode)
      if (value === PAY_MODE.DAY) return '日付'
      if ([PAY_MODE.WEEK, PAY_MODE.NATURAL_WEEK].includes(value)) return '周付'
      if ([PAY_MODE.FULL_MOON, PAY_MODE.NATURAL_MONTH].includes(value)) return '月付'
      const text = this.cleanAiText(payMode)
      if (!text) return ''
      if (/日付|日结|按日|每日/.test(text)) return '日付'
      if (/周付|周结|按周|每周/.test(text)) return '周付'
      if (/月付|自然月|满月|按月|每月/.test(text)) return '月付'
      return text
    },
    formatAiPlanRentPeriod(leaseInfo = {}, payModeName = '') {
      const rentPeriod = this.cleanAiText(leaseInfo.rentPeriod)
      if (!rentPeriod) return ''
      if (/天|日/.test(rentPeriod)) return rentPeriod.replace(/日/g, '天')
      if (/周|月|期/.test(rentPeriod)) return rentPeriod
      if (Number(leaseInfo.rentPeriodType) === 1 || payModeName === '日付') return `${rentPeriod}天`
      if (payModeName === '周付') return `${rentPeriod}周`
      return `${rentPeriod}期`
    },
    getAiPlanRentUnit(payModeName = '', rentPeriod = '') {
      if (/日付/.test(payModeName) || /天|日/.test(rentPeriod)) return '元/天'
      if (/周付/.test(payModeName) || /周/.test(rentPeriod)) return '元/周'
      return '元/期'
    },
    makeUniqueAiPlanName(baseName) {
      const cleanName = this.cleanAiText(baseName)
      if (!cleanName) return ''
      return cleanName.slice(0, 50)
    },
    buildAiFeeTableData(prefill = {}, directFeeList = null) {
      if (Array.isArray(directFeeList) && directFeeList.length) {
        const normalized = directFeeList
          .map((item) => this.normalizeAiFeeRow(item))
          .filter(Boolean)
        if (normalized.length) return normalized
      }

      const rows = []
      const category =
        prefill.leaseInfo?.rentCategory ||
        this.rentDateInfo.rentCategory ||
        CATEGORY_CODE_MAP.ZU_CHE
      const payMode =
        prefill.leaseInfo?.payMode ||
        this.rentDateInfo.payMode ||
        PAY_MODE.FULL_MOON
      const amounts = prefill.amounts || {}

      if (
        category === CATEGORY_CODE_MAP.YI_ZU_DAI_GOU &&
        amounts.fullPayment &&
        !amounts.downPayment &&
        !amounts.rentAmount
      ) {
        rows.push(
          this.buildConfirmedFeeRow(
            FEE_CODE_MAP.QUAN_KUAN,
            amounts.fullPayment,
            PAYMODE_CODE_MAP.DISPOSABLE,
            true,
          ),
        )
        return rows
      }

      if (
        (category === CATEGORY_CODE_MAP.ZU_CHE ||
          category === CATEGORY_CODE_MAP.YI_ZU_DAI_GOU) &&
        amounts.deposit
      ) {
        const depositPayMode =
          amounts.depositPaymentMethod === 'stages'
            ? PAYMODE_CODE_MAP.STAGES
            : PAYMODE_CODE_MAP.DISPOSABLE
        rows.push(
          this.buildConfirmedFeeRow(
            FEE_CODE_MAP.YA_JIN,
            amounts.deposit,
            depositPayMode,
            category === CATEGORY_CODE_MAP.ZU_CHE ||
              category === CATEGORY_CODE_MAP.YI_ZU_DAI_GOU,
          ),
        )
      }
      if (category === CATEGORY_CODE_MAP.YI_ZU_DAI_GOU && amounts.downPayment) {
        rows.push(
          this.buildConfirmedFeeRow(
            FEE_CODE_MAP.SHOU_FU_KUAN,
            amounts.downPayment,
            PAYMODE_CODE_MAP.DISPOSABLE,
            true,
          ),
        )
      }
      if (
        category !== CATEGORY_CODE_MAP.SI_JI_HE_ZUO_MO_SHI &&
        amounts.rentAmount
      ) {
        rows.push(
          this.buildConfirmedFeeRow(
            FEE_CODE_MAP.ZU_JIN,
            amounts.rentAmount,
            payMode,
            true,
          ),
        )
      }
      if (category === CATEGORY_CODE_MAP.YI_ZU_DAI_GOU && amounts.tailPayment) {
        rows.push(
          this.buildConfirmedFeeRow(
            FEE_CODE_MAP.WEI_KUAN,
            amounts.tailPayment,
            PAYMODE_CODE_MAP.DISPOSABLE,
            false,
          ),
        )
      }
      return rows
    },
    normalizeAiFeeRow(row = {}) {
      if (!row || typeof row !== 'object') return null
      const fundType = Number(row.fundType || row.type || row.rentPlanFeeType)
      const amount = this.parseAiAmountText(
        row.amount || row.planAmount || row.totalAmount,
      )
      if (!fundType || !amount) return null
      return this.buildConfirmedFeeRow(
        fundType,
        amount,
        Number(row.payMode || row.payMethod) ||
          this.rentDateInfo.payMode ||
          PAY_MODE.FULL_MOON,
        row.required !== undefined ? Boolean(row.required) : true,
        row,
      )
    },
    buildConfirmedFeeRow(fundType, amount, payMode, required, ext = {}) {
      const safeAmount = this.parseAiAmountText(amount)
      if (!safeAmount) return null
      const isDeposit = Number(fundType) === FEE_CODE_MAP.YA_JIN
      const isOneTime = [
        FEE_CODE_MAP.YA_JIN,
        FEE_CODE_MAP.SHOU_FU_KUAN,
        FEE_CODE_MAP.WEI_KUAN,
        FEE_CODE_MAP.QUAN_KUAN,
      ].includes(Number(fundType))
      const rowPayMode =
        isOneTime && !isDeposit
          ? PAYMODE_CODE_MAP.DISPOSABLE
          : Number(payMode || PAY_MODE.FULL_MOON)
      const row = {
        ...ext,
        fundType: Number(fundType),
        type: Number(fundType),
        name: this.getAiFeeName(fundType),
        required: Boolean(required),
        toast: ext.toast || '请配置收款项',
        isConfirm: true,
        payMode: rowPayMode,
        rentPayMode: this.rentDateInfo.payMode || PAY_MODE.FULL_MOON,
        payPlanType: ext.payPlanType ?? RENT_TYPE.FIXEDRENT,
        amountType: ext.amountType ?? AMOUNTTYPE_CODE_MAP.AMOUNT_PER_PERIOD,
        amountAccuracy: ext.amountAccuracy ?? 2,
        advancePayment:
          ext.advancePayment ??
          this.getAiDefaultAdvancePayment(fundType, rowPayMode),
        amount: safeAmount,
        totalAmount: ext.totalAmount || safeAmount,
        planAmount: safeAmount,
        items: Array.isArray(ext.items) ? ext.items : [],
      }
      if (isDeposit) {
        row.depositCollectRule =
          rowPayMode === PAYMODE_CODE_MAP.STAGES
            ? DEPOSIT_COLLECT_RULE.CUSTOM_STAGES
            : DEPOSIT_COLLECT_RULE.ONCE
        row.depositCollectRuleText =
          DEPOSIT_COLLECT_RULE_TEXT[row.depositCollectRule] || ''
      }
      return row
    },
    getAiFeeName(fundType) {
      const map = {
        [FEE_CODE_MAP.ZU_JIN]: '租金',
        [FEE_CODE_MAP.YA_JIN]: '押金',
        [FEE_CODE_MAP.SHOU_FU_KUAN]: '首付款',
        [FEE_CODE_MAP.QUAN_KUAN]: '全款',
        [FEE_CODE_MAP.WEI_KUAN]: '尾款',
      }
      return map[Number(fundType)] || '其他'
    },
    getAiDefaultAdvancePayment(fundType, payMode) {
      if (
        [
          FEE_CODE_MAP.YA_JIN,
          FEE_CODE_MAP.SHOU_FU_KUAN,
          FEE_CODE_MAP.QUAN_KUAN,
          FEE_CODE_MAP.WEI_KUAN,
        ].includes(Number(fundType))
      ) {
        return Number(payMode) === PAYMODE_CODE_MAP.DISPOSABLE
          ? ADVANCE_PAYMENT_CODE.ALL
          : ADVANCE_PAYMENT_CODE.FIRST_PERIOD
      }
      return ADVANCE_PAYMENT_CODE.NO_PAYMENT
    },
    mergeAiFeeRows(currentRows = [], aiRows = []) {
      const result = [...(currentRows || [])]
      aiRows.filter(Boolean).forEach((row) => {
        const key = String(row.fundType)
        const signature = this.getAiFeeRowSignature(row)
        const index = result.findIndex(
          (item) => Number(item.fundType) === Number(row.fundType),
        )
        if (index === -1) {
          result.push(row)
          this.aiAppliedFeeSignatures[key] = signature
          return
        }
        const currentSignature = this.getAiFeeRowSignature(result[index])
        if (
          !result[index].isConfirm ||
          currentSignature === this.aiAppliedFeeSignatures[key]
        ) {
          result.splice(index, 1, {
            ...result[index],
            ...row,
          })
          this.aiAppliedFeeSignatures[key] = signature
        }
      })
      return result
    },
    getAiFeeRowSignature(row = {}) {
      return JSON.stringify({
        fundType: row.fundType,
        amount: row.amount,
        totalAmount: row.totalAmount,
        payMode: row.payMode,
        payPlanType: row.payPlanType,
        amountType: row.amountType,
      })
    },
    shouldApplyAiValue(currentValue, nextValue, previousAiValue, defaultValue) {
      if (!this.hasMeaningfulAiValue(nextValue)) return false
      if (!this.hasMeaningfulAiValue(currentValue)) return true
      if (
        previousAiValue !== undefined &&
        this.isSameAiValue(currentValue, previousAiValue)
      ) return true
      if (
        defaultValue !== undefined &&
        this.isSameAiValue(currentValue, defaultValue)
      ) return true
      return false
    },
    hasMeaningfulAiValue(value) {
      if (Array.isArray(value)) return value.length > 0
      if (value && typeof value === 'object') return Object.keys(value).length > 0
      return this.cleanAiText(value) !== ''
    },
    isSameAiValue(prev, next) {
      return JSON.stringify(prev ?? '') === JSON.stringify(next ?? '')
    },
    cloneAiValue(value) {
      if (value && typeof value === 'object') {
        return JSON.parse(JSON.stringify(value))
      }
      return value
    },
    applyAiLeaseRules(analysisData = {}, summaryData = null) {
      const aiSource = {
        analysisData,
        summaryData,
      }
      const ruleData = {
        rentCarRuleTip: this.getAiValue(aiSource, [
          'rentCarRuleTip',
          '租车须知提示信息',
          '用车规则提示',
        ]),
        returnCarRuleTip: this.getAiValue(aiSource, [
          'returnCarRuleTip',
          '还车说明提示信息',
          '还车规则提示',
        ]),
        rentCarRule: this.getAiValue(aiSource, [
          'rentCarRule',
          '租车须知',
          '用车规则',
        ]),
        returnCarRule: this.getAiValue(aiSource, [
          'returnCarRule',
          '还车说明',
          '还车规则',
        ]),
      }
      const keyInfoRuleData = this.buildAiLeaseRuleDataFromKeyInfo(
        this.getAiKeyInfo(analysisData, summaryData),
        summaryData,
      )
      const mergedRuleData = this.mergeAiRuleData(keyInfoRuleData, ruleData)
      const hasRuleData =
        Object.values(ruleData).some((value) => {
          return value !== undefined && value !== null && value !== ''
        }) ||
        Object.values(keyInfoRuleData).some((value) => {
          return value && typeof value === 'object' && Object.keys(value).length
        })
      if (!hasRuleData) return
      this.applyLeaseRuleData(mergedRuleData)
    },
    buildAiLeaseRuleDataFromKeyInfo(keyInfo = {}, summaryData = null) {
      const tips = this.getAiTips(summaryData)
      const data = {
        rentCarRuleTip: {},
        returnCarRuleTip: {},
        rentCarRule: {},
        returnCarRule: {},
      }

      if (tips.violation_handling) data.rentCarRuleTip.violationTip = tips.violation_handling
      if (tips.accident_rules) data.rentCarRuleTip.insuranceTip = tips.accident_rules
      if (tips.usage_rules) {
        data.rentCarRuleTip.areaTip = tips.usage_rules
        data.rentCarRuleTip.distanceTip = tips.usage_rules
      }
      if (tips.maintenance_rules) data.rentCarRuleTip.maintenanceMainTip = tips.maintenance_rules
      if (tips.gps_policy) data.rentCarRuleTip.gpsTip = tips.gps_policy
      if (tips.document_rules) data.rentCarRuleTip.licenceTip = tips.document_rules
      if (tips.return_rules) {
        data.returnCarRuleTip.depositRefundTip = tips.return_rules
        data.returnCarRuleTip.returnEarlyTip = tips.return_rules
        data.returnCarRuleTip.returnBatteryTip = tips.return_rules
      }

      const violation = keyInfo.violation_handling || keyInfo.violationHandling
      if (violation) {
        data.rentCarRule.violation = {
          pointFeePerScore: this.parseAiAmountText(
            this.pickAiKeyInfoValue(violation, [
              '违章扣分费用',
              '扣分费用',
              '每分费用',
            ]),
          ),
          overdueFeeWithDeduction: this.parseAiAmountText(
            this.pickAiKeyInfoValue(violation, [
              '带抵扣逾期费',
              '有扣分处理费',
              '扣分处理费',
            ]),
          ),
          overdueFeeWithoutDeduction: this.parseAiAmountText(
            this.pickAiKeyInfoValue(violation, [
              '不带抵扣逾期费',
              '无扣分处理费',
              '处理费',
            ]),
          ),
        }
      }

      const accident = keyInfo.accident_rules || keyInfo.accidentRules
      if (accident) {
        const insuranceTypes = this.pickAiKeyInfoValue(accident, ['保险类型'])
        const hasCommercial = /商业|营运|三者|车损|盗抢|人员|不计免/.test(
          insuranceTypes,
        )
        data.rentCarRule.insurance = {
          strongRisk: 1,
          thirdParty: /三者|商业|营运/.test(insuranceTypes) ? 1 : 0,
          carDamage: /车损|商业|营运/.test(insuranceTypes) ? 1 : 0,
          carLost: /盗抢|商业|营运/.test(insuranceTypes) ? 1 : 0,
          people: /人员|商业|营运/.test(insuranceTypes) ? 1 : 0,
          nonDeductible: /不计免|免责|商业|营运/.test(insuranceTypes) ? 1 : 0,
          hasCommercialInsurance: hasCommercial,
          accidentLiabilityFullFee: this.parseAiAmountText(
            this.pickAiKeyInfoValue(accident, ['全责事故费']),
          ),
          vehicleDepreciationFullPercent: this.parseAiNumber(
            this.pickAiKeyInfoValue(accident, ['全责折旧比例']),
          ),
          accidentLiabilityMajorFee: this.parseAiAmountText(
            this.pickAiKeyInfoValue(accident, ['主责事故费']),
          ),
          vehicleDepreciationMajorPercent: this.parseAiNumber(
            this.pickAiKeyInfoValue(accident, ['主责折旧比例']),
          ),
          accidentLiabilitySecondaryFee: this.parseAiAmountText(
            this.pickAiKeyInfoValue(accident, ['次责事故费']),
          ),
          vehicleDepreciationSecondaryPercent: this.parseAiNumber(
            this.pickAiKeyInfoValue(accident, ['次责折旧比例']),
          ),
          accidentLiabilitySharedFee: this.parseAiAmountText(
            this.pickAiKeyInfoValue(accident, ['同责事故费']),
          ),
          vehicleDepreciationSharedPercent: this.parseAiNumber(
            this.pickAiKeyInfoValue(accident, ['同责折旧比例']),
          ),
          accidentLiabilityNoFee: this.parseAiAmountText(
            this.pickAiKeyInfoValue(accident, ['无责事故费']),
          ),
          vehicleDepreciationNoPercent: this.parseAiNumber(
            this.pickAiKeyInfoValue(accident, ['无责折旧比例']),
          ),
        }
      }

      const usage = keyInfo.usage_rules || keyInfo.usageRules
      if (usage) {
        const area = this.pickAiKeyInfoValue(usage, ['行驶区域'])
        const mileage = this.pickAiKeyInfoValue(usage, ['里程限制'])
        if (area) {
          data.rentCarRule.area = {
            limit: this.isAiUnlimited(area) ? 0 : 1,
            limitArea: this.isAiUnlimited(area) ? '' : area,
            fee: this.parseAiAmountText(
              this.pickAiKeyInfoValue(usage, ['行驶区域违约金']),
            ),
          }
        }
        if (mileage) {
          data.rentCarRule.distance = {
            limit: this.isAiUnlimited(mileage) ? 0 : 1,
            limitDistance: this.isAiUnlimited(mileage)
              ? ''
              : this.parseAiNumber(mileage),
            fee: this.parseAiAmountText(
              this.pickAiKeyInfoValue(usage, ['里程限制违约金']),
            ),
          }
        }
      }

      const maintenance = keyInfo.maintenance_rules || keyInfo.maintenanceRules
      if (maintenance) {
        const interval = this.parseAiNumber(
          this.pickAiKeyInfoValue(maintenance, ['保养周期']),
        )
        const fee = this.parseAiAmountText(
          this.pickAiKeyInfoValue(maintenance, ['保养逾期违约金']),
        )
        if (interval || fee) {
          data.rentCarRule.maintenanceMain = {
            maintenanceInterval: interval,
            maintenanceOverdueFee: fee,
          }
        }
      }

      const gps = keyInfo.gps_policy || keyInfo.gpsPolicy
      if (gps) {
        const fee = this.parseAiAmountText(
          this.pickAiKeyInfoValue(gps, ['GPS损坏赔偿', 'GPS损坏费用']),
        )
        if (fee) data.rentCarRule.gps = { gpsDamageFee: fee }
      }

      const documentRules = keyInfo.document_rules || keyInfo.documentRules
      if (documentRules) {
        const licence = {
          drivingLicenseReissueFee: this.parseAiAmountText(
            this.pickAiKeyInfoValue(documentRules, ['行驶证补办费']),
          ),
          vehicleRegistrationReissueFee: this.parseAiAmountText(
            this.pickAiKeyInfoValue(documentRules, [
              '车辆运营证补办费',
              '登记证补办费',
            ]),
          ),
          vehicleKeyReissueFee: this.parseAiAmountText(
            this.pickAiKeyInfoValue(documentRules, ['车钥匙补办费']),
          ),
          vehiclePlateReissueFee: this.parseAiAmountText(
            this.pickAiKeyInfoValue(documentRules, ['车牌补办费']),
          ),
        }
        if (Object.values(licence).some(Boolean)) {
          data.rentCarRule.licence = licence
        }
      }

      const returnRules = keyInfo.return_rules || keyInfo.returnRules
      if (returnRules) {
        const depositDays = this.parseAiNumber(
          this.pickAiKeyInfoValue(returnRules, ['押金退还天数']),
        )
        if (depositDays) data.returnCarRule.depositRefund = { depositRefundDays: depositDays }
        const earlyFee = this.parseAiAmountText(
          this.pickAiKeyInfoValue(returnRules, ['提前还车违约金']),
        )
        const earlyPercent = this.parseAiNumber(
          this.pickAiKeyInfoValue(returnRules, ['提前还车违约金比例']),
        )
        const earlyType = this.pickAiKeyInfoValue(returnRules, ['提前还车类型'])
        data.returnCarRule.returnEarly = {
          returnCarDeliveryFeeType:
            earlyFee || earlyPercent || /违约|赔偿|支付/.test(earlyType)
              ? 1
              : 0,
          returnCarDeliveryFee: earlyFee,
          returnCarDeliveryFeePercent: earlyPercent,
        }
        const batteryReq = returnRules['电量要求']
        const battery = Array.isArray(batteryReq) ? batteryReq[0] : batteryReq
        if (battery && typeof battery === 'object') {
          data.returnCarRule.returnBattery = {
            returnBatteryRemaining: this.parseAiNumber(battery.threshold),
            returnBatteryRemainingUnit: battery.unit || '%',
            batteryRechargeFee: this.parseAiAmountText(battery.fee),
          }
        }
      }

      return this.compactAiRuleData(data)
    },
    getAiTips(summaryData = null) {
      const parsedSummary = this.parseMaybeJson(summaryData) || {}
      return parsedSummary.ai_tips || parsedSummary.aiTips || {}
    },
    isAiUnlimited(value) {
      const text = this.cleanAiText(value)
      return !text || /不限|无限制|全国|未明确/.test(text)
    },
    mergeAiRuleData(base = {}, ext = {}) {
      const mergeObject = (prev, next) => {
        const result = { ...(prev || {}) }
        Object.entries(next || {}).forEach(([key, value]) => {
          if (value === undefined || value === null || value === '') return
          if (value && typeof value === 'object' && !Array.isArray(value)) {
            result[key] = mergeObject(result[key], value)
          } else {
            result[key] = value
          }
        })
        return result
      }
      return mergeObject(base, ext)
    },
    compactAiRuleData(data = {}) {
      const compact = (value) => {
        if (!value || typeof value !== 'object') return value
        const result = Array.isArray(value) ? [] : {}
        Object.entries(value).forEach(([key, item]) => {
          if (item && typeof item === 'object') {
            const child = compact(item)
            if (child && Object.keys(child).length) result[key] = child
          } else if (item !== undefined && item !== null && item !== '') {
            result[key] = item
          }
        })
        return result
      }
      return compact(data)
    },
    applyLeaseRuleData(ruleData = {}) {
      this.aiLeaseRulesData = {
        ...(this.aiLeaseRulesData || {}),
        ...ruleData,
      }
      this.$nextTick(() => {
        this.$refs.leaseRulesRef?.setFormData?.(this.aiLeaseRulesData)
      })
    },
    normalizeAiDateTime(value, isEndDate = false) {
      if (!value) return ''
      try {
        const dateValue = String(value)
          .trim()
          .replace(
            /(\d{4})\s*年\s*(\d{1,2})\s*月\s*(\d{1,2})\s*日/g,
            (_, y, m, d) => {
              return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(
                2,
                '0',
              )}`
            },
          )
          .replace(/\//g, '-')
        if (!dateValue) return ''
        if (/\d{1,2}:\d{1,2}/.test(dateValue)) {
          return dateValue
        }
        return `${dateValue} ${isEndDate ? '23:59:59' : '00:00:00'}`
      } catch (error) {
        return ''
      }
    },
    getAiFieldMappingValue(fieldExtract, groupName, fieldName) {
      return this.extractAiEntryValue(
        this.getAiFieldMappingEntry(fieldExtract, groupName, fieldName),
      )
    },
    normalizeAiLookupKey(key) {
      return String(key || '')
        .replace(/[\s:_：/\\-]/g, '')
        .toLowerCase()
    },
    parseMaybeJson(value) {
      if (typeof value !== 'string') return value
      const text = value.trim()
      if (!text) return value
      if (!text.startsWith('{') && !text.startsWith('[')) return value
      try {
        return JSON.parse(text)
      } catch (error) {
        return value
      }
    },
    normalizeAiValue(value) {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        const valueKeys = [
          'value',
          'content',
          'text',
          'result',
          'numbers',
          'amount',
          'date',
        ]
        for (const key of valueKeys) {
          if (
            value[key] !== undefined &&
            value[key] !== null &&
            value[key] !== ''
          ) {
            return value[key]
          }
        }
        if (value.raw && typeof value.raw === 'object') {
          return this.normalizeAiValue(value.raw)
        }
      }
      return value
    },
    getAiValue(source, names = []) {
      if (!source) return ''
      const targetNames = names.map((name) => this.normalizeAiLookupKey(name))
      const visited = new Set()
      const stack = [source]

      while (stack.length) {
        const item = stack.shift()
        if (!item || typeof item !== 'object') continue
        if (visited.has(item)) continue
        visited.add(item)

        if (Array.isArray(item)) {
          item.forEach((child) => stack.push(child))
          continue
        }

        const label =
          item.label ||
          item.name ||
          item.title ||
          item.fieldName ||
          item.keyName ||
          item.key
        if (label && targetNames.includes(this.normalizeAiLookupKey(label))) {
          const labelValue = this.normalizeAiValue(item)
          if (
            labelValue !== item &&
            labelValue !== undefined &&
            labelValue !== null &&
            labelValue !== ''
          ) {
            return labelValue
          }
        }

        const entries = Object.entries(item)
        for (const [key, value] of entries) {
          if (
            targetNames.includes(this.normalizeAiLookupKey(key)) &&
            value !== undefined &&
            value !== null &&
            value !== ''
          ) {
            return this.normalizeAiValue(value)
          }
        }
        entries.forEach(([, value]) => {
          if (value && typeof value === 'object') {
            stack.push(value)
          }
        })
      }
      return ''
    },
    parseAiNumber(value) {
      if (value === undefined || value === null || value === '') return ''
      if (typeof value === 'number') return value
      const match = String(value).match(/\d+(\.\d+)?/)
      return match ? Number(match[0]) : ''
    },
    parseAiRentCategory(value) {
      if (value === undefined || value === null || value === '') return ''
      const numberValue = Number(value)
      const validRentCategories = [
        CATEGORY_CODE_MAP.ZU_CHE,
        CATEGORY_CODE_MAP.YI_ZU_DAI_GOU,
        CATEGORY_CODE_MAP.SI_JI_HE_ZUO_MO_SHI,
      ]
      if (validRentCategories.includes(numberValue)) {
        return numberValue
      }
      const text = String(value)
      if (text.includes('以租代购') || text.includes('租买')) return CATEGORY_CODE_MAP.YI_ZU_DAI_GOU
      if (text.includes('司机') || text.includes('合作')) return CATEGORY_CODE_MAP.SI_JI_HE_ZUO_MO_SHI
      if (
        text.includes('纯租') ||
        text.includes('租车') ||
        text.includes('租赁')
      ) return CATEGORY_CODE_MAP.ZU_CHE
      return ''
    },
    parseAiRentPeriod(periodValue, periodTypeValue) {
      const periodText =
        periodValue === undefined || periodValue === null
          ? ''
          : String(periodValue)
      const periodTypeText =
        periodTypeValue === undefined || periodTypeValue === null
          ? ''
          : String(periodTypeValue)
      let rentPeriod = this.parseAiNumber(periodValue)
      let rentPeriodType = Number(periodTypeValue) || ''

      if (!rentPeriodType) {
        if (periodText.includes('年')) {
          rentPeriodType = 3
          rentPeriod = rentPeriod ? rentPeriod * 12 : rentPeriod
        } else if (periodText.includes('月') || periodTypeText.includes('月')) {
          rentPeriodType = 3
        } else if (
          periodText.includes('日') ||
          periodText.includes('天') ||
          periodTypeText.includes('日') ||
          periodTypeText.includes('天')
        ) {
          rentPeriodType = 1
        }
      }

      return {
        rentPeriod,
        rentPeriodType: [1, 3].includes(Number(rentPeriodType))
          ? Number(rentPeriodType)
          : '',
      }
    },
    parseAiPayMode(value) {
      if (value === undefined || value === null || value === '') return ''
      const numberValue = Number(value)
      if (
        [
          PAY_MODE.WEEK,
          PAY_MODE.FULL_MOON,
          PAY_MODE.NATURAL_MONTH,
          PAY_MODE.DAY,
          PAY_MODE.NATURAL_WEEK,
        ].includes(numberValue)
      ) {
        return numberValue
      }
      const text = String(value)
      if (text.includes('自然月')) return PAY_MODE.NATURAL_MONTH
      if (text.includes('满月')) return PAY_MODE.FULL_MOON
      if (text.includes('自然周')) return PAY_MODE.NATURAL_WEEK
      if (text.includes('满周')) return PAY_MODE.WEEK
      if (text.includes('周')) return PAY_MODE.WEEK
      if (text.includes('日')) return PAY_MODE.DAY
      if (text.includes('月')) return PAY_MODE.FULL_MOON
      return ''
    },
  },
}
</script>

<style lang="scss" scoped>
.rental-plan-step {
  padding: 0 0 24px;
}
.content-box {
  margin-bottom: 20px;

  &__title {
    display: flex;
    align-items: center;
    height: 38px;
    background: #f7f9fd;
    color: #1d2129;
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;

    .icon {
      width: 4px;
      height: 16px;
      margin-right: 8px;
      background: #2a6bff;
    }
  }

  &__content {
    width: 720px;
    margin: 0 auto;
    padding: 16px 0 8px;
  }
}
.content-item {
  .title {
    margin-bottom: 11px;
    color: #1d2129;
    font-size: 14px;
    font-weight: 500;
    line-height: 22px;
  }
}
.basic-info {
  $basic-info-column-gap: 32px;

  ::v-deep .el-column-box {
    gap: 0 $basic-info-column-gap;
  }
  ::v-deep .column-box {
    gap: 0 $basic-info-column-gap;
  }
  ::v-deep .el-form-item {
    display: flex;
    flex-direction: column;
    width: calc((100% - #{$basic-info-column-gap}) / 2) !important;
  }
  ::v-deep .el-form-item__label {
    justify-content: flex-start;
  }
}
::v-deep .el-table {
  margin-top: 4px;
}
::v-deep .planSetup-setupBox-operation {
  margin-top: 12px;
}
.store-select-wrap {
  display: flex;
  gap: 12px;
  width: 100%;
  .store-name-select {
    flex: 1;
  }
}
.store-transfer-dialog {
  ::v-deep .el-dialog {
    width: fit-content;
    min-width: 520px;
  }
}
.form-box {
  ::v-deep .el-form-item__label {
    justify-content: flex-start;
  }
}
</style>
